import { createSelector, createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';

import type { RootState } from '../../main/store/root.store';
import { getStorageValue, setStorageValue } from '../../shared/utils/storage';

export enum WalletType {
    MAIN = 'main',
    BONUS = 'bonus',
    LOCKED = 'locked',
}

export interface IWallet {
    walletId?: string | null;
    type?: string | null;
    isLocked?: boolean | null;
    isActive?: boolean | null;
    balanceAmount?: number | null;
    currencyCode?: string | null;
    restrictions?: {
        monetaryDebit?: Array<{
            currencyCode?: string;
            amount?: number;
            expiresOn?: string;
        }>;
    };
    aggregates?: any;
}

interface IAccountStore {
    hasController: boolean;
    wallets?: IWallet[];
    currency?: string;
}

const INITIAL_STATE = {
    wallets: undefined,
    hasController: false,
    currency: getStorageValue('currency'),
} as IAccountStore;

export const accountStore = createSlice({
    name: 'account',
    initialState: INITIAL_STATE,
    reducers: {
        reset(state) {
            state.wallets = [];
            state.hasController = false;
        },
        setHasController(state, action: PayloadAction<boolean>) {
            state.hasController = action.payload;
        },
        setCurrency(state, action: PayloadAction<string>) {
            state.currency = action.payload;
            setStorageValue('currency', action.payload);
        },
        setWallets(state, { payload }: PayloadAction<IWallet[]>) {
            state.wallets = payload;
        },
        updateWalletBalance(state, { payload }: PayloadAction<IWallet>) {
            if (!state.wallets) {
                return;
            }

            const found = state.wallets.find(({ walletId }) => walletId === payload.walletId);

            if (!found) {
                state.wallets.push(payload);
                return;
            }

            found.balanceAmount = payload.balanceAmount;
            found.isActive = payload.isActive;
            found.isLocked = payload.isLocked;
            found.currencyCode = payload.currencyCode;
            found.type = payload.type;
            found.aggregates = payload.aggregates;

            if (payload.restrictions) {
                found.restrictions = {
                    monetaryDebit: payload.restrictions.monetaryDebit ?? [],
                };
            }
        },
    },
});

const selectAccountStore = (state: RootState) => {
    return state[accountStore.name];
};

export const selectHasController = createSelector(
    [selectAccountStore],
    (state) => state.hasController,
);

export const selectWallets = createSelector([selectAccountStore], (state) => {
    return state.wallets;
});

export const selectCurrency = createSelector([selectAccountStore], (state) => {
    return state.currency;
});
